import React from 'react';
import { createRoot } from 'react-dom/client';
import App from 'components/App';
import 'index.css';
import 'typeface-roboto';
import { createBrowserHistory } from 'history';
import { configureStore } from 'configureStore';
import { Provider } from 'react-redux';
import { Route } from 'react-router-dom';
import { ConnectedRouter } from 'connected-react-router';
import { initialState } from 'store/initialState';
import { dataGridExtension } from 'components/devExtreme';
import { setUser, setConnectionState } from 'store/actions';
import { fetchWithToken } from 'auth';
import { sendClientLog } from 'services/logService';
import config from 'devextreme/core/config'; 
import { oktaAuth } from 'auth/index';
import { toRelativeUrl } from '@okta/okta-auth-js';
import { LoginCallback, Security } from '@okta/okta-react';
import * as revueHubConnection from 'signalR/revueHubConnection';

export var googleMapsApiKey;

let disconnectTimer;

const startApp = async () => {
  const history = createBrowserHistory();
  const store = configureStore(history, initialState);
  if (await oktaAuth.isAuthenticated()) {
    store.dispatch(setConnectionState('connected'));
  }
  oktaAuth.tokenManager.on('added', function (key, addedToken) {
    if (key !== 'accessToken') return;
    console.log('Access token has been added');
    if (disconnectTimer) {
      clearTimeout(disconnectTimer);
    }
    if (store.getState().appState.connectionState !== 'connected') {
      store.dispatch(setConnectionState('connected'));
      revueHubConnection.start();
      console.log('Starting revueHubConnection')
    }
  });
  oktaAuth.tokenManager.on('expired', function (key, expiredToken) {
    if (key !== 'accessToken') return;
    console.log('Access token has expired');
  });
  oktaAuth.tokenManager.on('renewed', function (key, newToken, oldToken) {
    if (key !== 'accessToken') return;
    console.log('Access token has been renewed');
  });
  oktaAuth.tokenManager.on('error', async function (err) {
    console.log('TokenManager error:', err);
    const refreshTokenExpiredMessage = 'The refresh token is invalid or expired.';
    if (err.message === refreshTokenExpiredMessage) return; // Don't disconnect on refresh token expired message- allow timer to run and disconnect if no new refresh token is obtained
    if (store.getState().appState.connectionState !== 'expired') {
      store.dispatch(setConnectionState('expired'));
      revueHubConnection.connection.stop();
      console.log('Stopping revueHubConnection');
    }
  });
  oktaAuth.tokenManager.on('removed', function (key, removedToken) {
    if (key !== 'accessToken') return;
    console.log('Access token has been removed');
    disconnectTimer = setTimeout(() => {
      if (store.getState().appState.connectionState !== 'expired') {
        console.log('Timer has expired without new token received, disconnecting..', Date.now());
        store.dispatch(setConnectionState('expired'));
        revueHubConnection.connection.stop();
      }
    }, 30000)
  });
  const response = await fetchWithToken(`/api/clientusers/whoami`);
  const { keys, ...user } = await response.json();
  googleMapsApiKey = keys.googleMapsApiKey;
  config({ licenseKey: keys.devextremeKey }); // Configure license for devexpress
  sendClientLog('Info', 'App Started');
  user.roles = user.userRoles.reduce((result, role) => {
    result[role.roleName.charAt(0).toLowerCase() + role.roleName.slice(1)] = role.isOktaRole;
    return result;
  }, {});
  store.dispatch(setUser(user));

  dataGridExtension.registerModule();

  const restoreOriginalUri = async (_oktaAuth, originalUri) => {
    history.replace(toRelativeUrl(originalUri || '/', window.location.origin));
  };

  return (
    createRoot(document.getElementById('root')).render(
      <Provider store={store}>
        <ConnectedRouter history={history}>
          {/* <Route path="/" component={App} /> */}
          <Security
            oktaAuth={oktaAuth}
            restoreOriginalUri={restoreOriginalUri}
          >
            <Route path="/login/callback" component={LoginCallback}/>
            <Route path="/" component={App} />         
          </Security>
        </ConnectedRouter>
      </Provider>
    )
  );
}

if (oktaAuth.isLoginRedirect()) {
  const originalUri = oktaAuth.getOriginalUri()
  await oktaAuth.handleRedirect(originalUri).then(function() {
    startApp();
  }).catch(e => {
    console.log(e.message);
    const userNotAssignedMessage = 'User is not assigned to the client application.';
    if (e.message === userNotAssignedMessage) {
      alert('You do not have access to this application.');
      oktaAuth.signOut({ clearTokensBeforeRedirect: true });
    }
  });
} else if (!await oktaAuth.isAuthenticated()) {
  const originalUri = toRelativeUrl(window.location.href, window.location.origin);
  oktaAuth.setOriginalUri(originalUri);
  oktaAuth.signInWithRedirect()
  .then(function () {
  }).catch(e => {
    console.log(e)
  });
} else {
  startApp();
}
